import React from 'react';
import { useSelector } from 'react-redux';
import { gql, useQuery } from '@apollo/client';
import Home, { HOME_ONBOARDING_QUERY } from './routes/Home';
import Dashboard from './routes/Dashboard';
import DashboardAdmin from './routes/DashboardAdmin';
import { isDashboardUserPath } from './lib/dashboard';
import Callback from './routes/Callback';
import Orders from './routes/Orders';
import OrderView from './routes/OrderView';
import Devices from './routes/Devices';
import Login from './routes/Login';
import KaAssignments from './routes/KaAssignments';
import Header from './components/Header';
import Footer from './components/Footer';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import PrivateRoute from './lib/PrivateRoute';
import PublicRoute from './lib/PublicRoute';
import ga from './lib/ga';

type Location = {
  pathname: string;
};

const fixedFooterRoutes: string[] = ['/login'];

// The only pages a user can open while their onboarding isn't finished. /devices stays open so a customer
// setting up their own devices can check them (it still needs MANAGE_DEVICES).
const ONBOARDING_PATHS: string[] = ['/', '/login', '/callback', '/devices'];

// GRI-957: `dashboard` is non-null only for dashboard users (their companyCode is a company with
// the weekly dashboard on). They see the dashboard and nothing else: no VIDEO, and any other
// page sends them to /dashboard. The API forbids them the order and video queries as well.
export const DASHBOARD_LINK_QUERY = gql`
  query DashboardLink {
    dashboard(weeks: 1) {
      publishedThrough
    }
  }
`;

function App({ location }: { location: Location }) {
  ga.usePageViews();
  const isLoggedIn = useSelector((state: any) => state.session.isLoggedIn);
  // Same query as the checklist on /, so both share Apollo's cache (and its polling).
  const { data, loading } = useQuery(HOME_ONBOARDING_QUERY, {
    skip: !isLoggedIn,
  });
  // Fails open like Home: no answer from the API means no redirect.
  const onboardingPending = Boolean(data?.viewer?.onboarding);
  // An API without `dashboard` (or an error) means a regular user.
  const { data: dashboardData, loading: dashboardLoading } = useQuery(
    DASHBOARD_LINK_QUERY,
    { skip: !isLoggedIn }
  );
  const dashboardUser = Boolean(dashboardData?.dashboard);

  return (
    <div>
      {/* Also hidden while loading, so a trial or dashboard user doesn't see VIDEO flash in and out */}
      <Header
        hideVideo={
          loading || onboardingPending || dashboardLoading || dashboardUser
        }
        showDashboard={dashboardUser}
      />
      {dashboardUser && !isDashboardUserPath(location.pathname) ? (
        <Redirect to="/dashboard" />
      ) : onboardingPending && !ONBOARDING_PATHS.includes(location.pathname) ? (
        <Redirect to="/" />
      ) : (
        <Switch>
          {/* Private routes */}
          <PrivateRoute exact path="/" component={Home} />
          <PrivateRoute
            exact
            path="/devices"
            requiredCapability="MANAGE_DEVICES"
            component={Devices}
          />
          <PrivateRoute
            exact
            path="/dashboard/admin"
            requiredCapability="PUBLISH_DASHBOARD"
            component={DashboardAdmin}
          />
          <PrivateRoute exact path="/dashboard" component={Dashboard} />
          <PrivateRoute
            exact
            path="/dashboard/locations/:locationId"
            component={Dashboard}
          />
          {/* Public routes */}
          <PublicRoute exact path="/orders" component={Orders} />
          <PublicRoute exact path="/orders/:orderId" component={Orders} />
          <PublicRoute
            exact
            path="/orders/:orderId/station/:stationId"
            component={Orders}
          />
          <PublicRoute
            exact
            path="/orders/:orderId/station/:stationId/vt/:videoTime"
            component={Orders}
          />
          <PublicRoute
            exact
            path="/order-view/:orderId"
            component={OrderView}
          />
          <PublicRoute
            exact
            path="/order-view/:orderId/station/:stationId"
            component={OrderView}
          />
          <PublicRoute
            exact
            path="/order-view/:orderId/station/:stationId/vt/:videoTime"
            component={OrderView}
          />
          <PublicRoute
            restricted={true}
            exact
            path="/login"
            component={Login}
          />
          <PublicRoute
            restricted={false}
            exact
            path="/callback"
            component={Callback}
          />
          {/* Links in KA WhatsApp reminders (GRI-253); no portal login needed */}
          <Route path="/assignments" component={KaAssignments} />
        </Switch>
      )}
      <Footer
        fixedPosition={!fixedFooterRoutes.indexOf(location.pathname)}
        showLanguage={onboardingPending && location.pathname === '/'}
      />
    </div>
  );
}

export default withRouter(App);
